/**
 * Where the console talks to and as whom. The cell URL and bearer token live
 * in sessionStorage (this tab only, gone when it closes); nothing is baked into
 * the bundle. The admin install credential is never persisted: it is held in
 * memory for one save (see `adminCredential`).
 */
export interface Connection {
  /** Cell base URL, e.g. `https://cell.example.com`. */
  readonly baseUrl: string
  /** Bearer token for a gated host (OIDC access token or demo bearer). */
  readonly token: string | null
  /** `x-norn-actor`, honoured only by a cell in explicit development mode. */
  readonly actor: string | null
}

/** An explicit org/environment pair; every scoped call carries both. */
export interface Scope {
  readonly orgId: string
  readonly environmentId: string
}

const CONNECTION_KEY = "norn.admin.connection/1"
const RECENT_SCOPES_KEY = "norn.admin.recent-scopes/1"
const MAX_RECENT = 8

export function normalizeBaseUrl(input: string): string {
  const trimmed = input.trim().replace(/\/+$/, "")
  const url = new URL(trimmed)
  if (url.protocol !== "http:" && url.protocol !== "https:") {
    throw new Error("The cell URL must start with http:// or https://")
  }
  return url.origin + url.pathname.replace(/\/+$/, "")
}

function blankToNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}

export function loadConnection(storage: Storage = sessionStorage): Connection | null {
  const raw = storage.getItem(CONNECTION_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<Record<keyof Connection, unknown>>
    if (typeof parsed.baseUrl !== "string") return null
    return {
      baseUrl: parsed.baseUrl,
      token: typeof parsed.token === "string" ? blankToNull(parsed.token) : null,
      actor: typeof parsed.actor === "string" ? blankToNull(parsed.actor) : null,
    }
  } catch {
    storage.removeItem(CONNECTION_KEY)
    return null
  }
}

export function saveConnection(connection: Connection, storage: Storage = sessionStorage): void {
  storage.setItem(CONNECTION_KEY, JSON.stringify(connection))
}

export function clearConnection(storage: Storage = sessionStorage): void {
  storage.removeItem(CONNECTION_KEY)
}

/** The cell admits any nonblank scope id (scope.rs escapes `/` and `%`); refuse only blanks, padding and control characters. */
export function isValidScopeId(value: string): boolean {
  // eslint-disable-next-line no-control-regex -- control characters are exactly what is refused
  return value.length > 0 && value.length <= 256 && value.trim() === value && !/[\u0000-\u001f\u007f]/.test(value)
}

/** Recently used scopes. Identifiers only, never credentials, so localStorage is fine. */
export function loadRecentScopes(storage: Storage = localStorage): Scope[] {
  try {
    const parsed: unknown = JSON.parse(storage.getItem(RECENT_SCOPES_KEY) ?? "[]")
    if (!Array.isArray(parsed)) return []
    return parsed.flatMap((entry: unknown) => {
      if (typeof entry !== "object" || entry === null) return []
      const { orgId, environmentId } = entry as Record<string, unknown>
      return typeof orgId === "string" && typeof environmentId === "string" && isValidScopeId(orgId) && isValidScopeId(environmentId) ? [{ orgId, environmentId }] : []
    })
  } catch {
    return []
  }
}

export function rememberScope(scope: Scope, storage: Storage = localStorage): Scope[] {
  const next = [scope, ...loadRecentScopes(storage).filter((s) => s.orgId !== scope.orgId || s.environmentId !== scope.environmentId)].slice(0, MAX_RECENT)
  try {
    storage.setItem(RECENT_SCOPES_KEY, JSON.stringify(next))
  } catch {
    // Recents are a convenience; blocked or full storage must not stop the console.
  }
  return next
}

/** A short, non-reversible label for the active token, so operators can tell sessions apart. */
export function tokenLabel(token: string | null): string {
  if (!token) return "No token"
  return `Bearer ···${token.slice(-4)}`
}
